import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {useEffect, useState} from "react";
import {
    BulkDeleteButton,
    Button as RaButton,
    ButtonProps,
    useGetList,
    useListContext,
    useNotify,
    useUnselectAll
} from "react-admin";
import {FiGitBranch, FiPlusCircle} from "react-icons/fi";
import {clientNoJson, endpoint} from "../../dataProvider";

type SubjectChoice = { id: number; label: string };
type Configuration = { id: number; configuration: string; children_count: number; main_config?: number | null };

const getErrorMessage = (error: any, fallback: string) => {
    const payload = error?.response?.data;
    if (typeof payload === "string") return payload;
    if (Array.isArray(payload) && payload[0]) return payload[0];
    return payload?.detail || payload?.non_field_errors?.[0] || error?.message || fallback;
};

const ClientSelector = ({value, onChange}: {value: SubjectChoice | null; onChange: (value: SubjectChoice | null) => void}) => {
    const [query, setQuery] = useState("");
    const [options, setOptions] = useState<SubjectChoice[]>([]);

    useEffect(() => {
        if (!query.trim()) {
            setOptions([]);
            return;
        }
        const timer = window.setTimeout(() => {
            clientNoJson(`${endpoint}/autocomplete/subject/?type=client&search=${encodeURIComponent(query)}`, {method: "GET"})
                .then(({data}) => setOptions(data.results || data || []))
                .catch(() => setOptions([]));
        }, 250);
        return () => window.clearTimeout(timer);
    }, [query]);

    return <Autocomplete options={options} value={value} getOptionLabel={(option) => option.label}
                         onChange={(_, item) => onChange(item)} onInputChange={(_, text) => setQuery(text)}
                         renderInput={(params) => <TextField {...params} label="Cliente" variant="outlined" fullWidth required/>}/>;
};

export const CreateNoteConfigurationGroupButton = (props: ButtonProps) => {
    const {selectedIds, refetch} = useListContext();
    const unselectAll = useUnselectAll("note-configurations");
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [client, setClient] = useState<SubjectChoice | null>(null);
    const [saving, setSaving] = useState(false);
    const close = (force = false) => {
        if (saving && !force) return;
        setOpen(false);
        setName("");
        setClient(null);
    };
    const create = async () => {
        if (!name.trim() || !client) {
            notify("Inserisci il nome del gruppo e il cliente", "warning");
            return;
        }
        setSaving(true);
        try {
            await clientNoJson(`${endpoint}/note-configurations/groups/`, {
                method: "POST", data: {name: name.trim(), client: client.id, configurations: selectedIds}
            });
            notify("Gruppo configurazioni creato", "info");
            unselectAll();
            refetch();
            close(true);
        } catch (error: any) {
            notify(getErrorMessage(error, "Impossibile creare il gruppo"), "warning");
        } finally {
            setSaving(false);
        }
    };
    return <>
        <RaButton {...props} label="Crea gruppo" disabled={saving || !selectedIds.length} onClick={() => setOpen(true)}><FiGitBranch/></RaButton>
        <Dialog open={open} onClose={() => close()} fullWidth maxWidth="sm">
            <DialogTitle>Crea gruppo configurazioni</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>{selectedIds.length} configurazioni selezionate</Typography>
                <TextField label="Nome gruppo" value={name} onChange={(event) => setName(event.target.value)} variant="outlined" fullWidth required style={{margin: "12px 0"}}/>
                <ClientSelector value={client} onChange={setClient}/>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => close()} disabled={saving}>Annulla</Button>
                <Button color="primary" variant="contained" onClick={create} disabled={saving}>Crea gruppo</Button>
            </DialogActions>
        </Dialog>
    </>;
};

export const AddToNoteConfigurationGroupButton = (props: ButtonProps) => {
    const {selectedIds, refetch} = useListContext();
    const unselectAll = useUnselectAll("note-configurations");
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [group, setGroup] = useState<Configuration | null>(null);
    const [saving, setSaving] = useState(false);
    const {data = {}, ids = []} = useGetList("note-configurations", {page: 1, perPage: 250}, {field: "configuration", order: "ASC"}, {hide: false});
    const groups = ids.map((id) => (data as any)[id] as Configuration)
        .filter((item) => !item.main_config && item.children_count > 0 && !selectedIds.includes(item.id));
    const close = (force = false) => {
        if (saving && !force) return;
        setOpen(false);
        setGroup(null);
    };
    const add = async () => {
        if (!group) {
            notify("Seleziona un gruppo", "warning");
            return;
        }
        setSaving(true);
        try {
            await clientNoJson(`${endpoint}/note-configurations/${group.id}/add-configurations/`, {
                method: "POST", data: {configurations: selectedIds}
            });
            notify("Configurazioni aggiunte al gruppo", "info");
            unselectAll();
            refetch();
            close(true);
        } catch (error: any) {
            notify(getErrorMessage(error, "Impossibile aggiungere le configurazioni al gruppo"), "warning");
        } finally {
            setSaving(false);
        }
    };
    return <>
        <RaButton {...props} label="Aggiungi a gruppo" disabled={saving || !selectedIds.length} onClick={() => setOpen(true)}><FiPlusCircle/></RaButton>
        <Dialog open={open} onClose={() => close()} fullWidth maxWidth="sm">
            <DialogTitle>Aggiungi configurazioni a un gruppo</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>{selectedIds.length} configurazioni selezionate</Typography>
                <Autocomplete options={groups} value={group} getOptionLabel={(item) => item.configuration}
                              onChange={(_, item) => setGroup(item)}
                              renderInput={(params) => <TextField {...params} label="Gruppo" variant="outlined" fullWidth required style={{marginTop: 12}}/>}/>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => close()} disabled={saving}>Annulla</Button>
                <Button color="primary" variant="contained" onClick={add} disabled={saving}>Aggiungi</Button>
            </DialogActions>
        </Dialog>
    </>;
};
